/**
 * Imperative direct-pixel grip on a panel's right edge.
 *
 * The drag never touches React until release: slots track the pointer through
 * `--slot-preview-*` variables (main[data-resizing] makes the panels read
 * them), so an unrelated re-render mid-drag can rewrite the committed
 * `--slot-*` pair without disturbing the preview. On release the final
 * geometry is handed to both pairs before the attribute comes off, and React
 * then commits the same pixels — nothing is left for the edge transitions to
 * animate.
 *
 * `panels` is the whole row including the trailing free slot, so the handle
 * after the last panel has something to drag against; that slot carries no id
 * and never receives a width (see panelResize.ts).
 */

import type {
    PointerEvent as ReactPointerEvent, ReactElement, RefObject,
} from 'react';
import React, { useLayoutEffect, useRef } from 'react';

import { usePointerDrag } from '@/hooks/usePointerDrag';
import type { PlacedResizeSlot as PlacedSlot, ResizeSlot } from '@/types/panels';

import { positionPanels } from './allocate';
import {
    cancelPanelPreview,
    clearPanelPreviews,
    finishPanelHandoff,
    getPanelBoundaryStyle,
    writePanelBoundaryHandoff,
    writePanelBoundaryPreview,
    writePanelSlotHandoff,
    writePanelSlotPreview,
} from './panelGeometry';
import { applyResize, dragUpdates } from './panelResize';
import { beginStageResize, endStageResize } from './stageMotion';

interface PanelResizeHandleProps {
    /** The whole row, trailing free slot included. */
    panels: ResizeSlot[];
    /** Index of the first slot right of the handle. */
    boundaryIndex: number;
    boundaryStartPx: number;
    stageWidth: number;
    stageRef: RefObject<HTMLElement | null>;
    commitDragWidths: (updates: Record<string, number>, layout: PlacedSlot[]) => void;
    resetPanelWidth: (id: string) => void;
}

/** The live drag: where it began, and the geometry already written out. */
interface ResizeDrag {
    startX: number;
    snapshot: ResizeSlot[];
    /** Last geometry written per element, so a frame skips what has not moved. */
    written: Map<string, string>;
    frame: number | null;
    pendingX: number | null;
}

function cancelResize(
    drag: RefObject<ResizeDrag | null>, stage: HTMLElement | null,
): void {
    if (!cancelPanelPreview(drag, stage)) {
        return;
    }
    endStageResize();
}

export const PanelResizeHandle = ({
    panels,
    boundaryIndex,
    boundaryStartPx,
    stageWidth,
    stageRef,
    commitDragWidths,
    resetPanelWidth,
}: PanelResizeHandleProps): ReactElement => {
    const drag = useRef<ResizeDrag | null>(null);
    const panel = panels[boundaryIndex - 1];

    const applyPreview = (placedSlots: PlacedSlot[], isHandoff = false): void => {
        const stage = stageRef.current;
        const activeResizeDrag = drag.current;
        if (!stage || !activeResizeDrag) {
            return;
        }
        const writeSlot = isHandoff ? writePanelSlotHandoff : writePanelSlotPreview;
        const writeBoundary = isHandoff ? writePanelBoundaryHandoff : writePanelBoundaryPreview;

        for (const item of placedSlots) {
            if (item.id === null) {
                continue; //< The free slot has no element to move.
            }
            const signature = `${item.startPx}|${item.widthPx}`;
            if (activeResizeDrag.written.get(item.id) === signature) {
                continue;
            }
            activeResizeDrag.written.set(item.id, signature);
            const element = stage.querySelector<HTMLElement>(`[data-panel-id="${item.id}"]`);
            if (element) {
                writeSlot(element, item);
            }
        }

        for (const element of stage.querySelectorAll<HTMLElement>('.panel-resize-handle')) {
            const boundary = Number(element.dataset.boundary);
            const right = placedSlots[boundary];
            if (!right) {
                continue;
            }
            const signature = `h|${right.startPx}`;
            if (activeResizeDrag.written.get(`#h${boundary}`) === signature) {
                continue;
            }
            activeResizeDrag.written.set(`#h${boundary}`, signature);
            writeBoundary(element, right.startPx);
        }
    };

    const resolveDrag = (
        activeResizeDrag: ResizeDrag, clientX: number,
    ): { updates: Record<string, number>; layout: PlacedSlot[] } => {
        const { snapshot, startX } = activeResizeDrag;
        const widths = applyResize(snapshot, boundaryIndex, clientX - startX);
        return {
            updates: dragUpdates(snapshot, widths),
            layout: positionPanels(snapshot.map(
                (slot, index) => ({ ...slot, widthPx: widths[index] ?? slot.widthPx }))),
        };
    };

    const { onPointerDown } = usePointerDrag({
        onStart: (event: ReactPointerEvent<HTMLDivElement>): boolean => {
            const stage = stageRef.current;
            if (!stage) {
                return false;
            }

            drag.current = {
                startX: event.clientX,
                snapshot: panels.map((slot) => ({ ...slot })),
                written: new Map(),
                frame: null,
                pendingX: null,
            };
            clearPanelPreviews(stage);
            stage.setAttribute('data-resizing', '');
            beginStageResize();
            return true;
        },

        onMove: (event: PointerEvent): void => {
            const activeResizeDrag = drag.current;
            if (!activeResizeDrag) {
                return;
            }
            activeResizeDrag.pendingX = event.clientX;
            if (activeResizeDrag.frame !== null) {
                return;
            }
            activeResizeDrag.frame = requestAnimationFrame(() => {
                const currentResizeDrag = drag.current;
                if (!currentResizeDrag) {
                    return;
                }
                currentResizeDrag.frame = null;
                // No pointer sample yet means no movement, so resolve against the start.
                applyPreview(resolveDrag(currentResizeDrag,
                    currentResizeDrag.pendingX ?? currentResizeDrag.startX).layout);
            });
        },

        onCancel: (): void => cancelResize(drag, stageRef.current),

        onEnd: (event: PointerEvent): void => {
            const activeResizeDrag = drag.current;
            if (!activeResizeDrag) {
                return;
            }
            if (activeResizeDrag.frame !== null) {
                cancelAnimationFrame(activeResizeDrag.frame);
            }
            const { updates, layout } = resolveDrag(activeResizeDrag, event.clientX);
            activeResizeDrag.written.clear();
            applyPreview(layout, true);
            drag.current = null;
            commitDragWidths(updates, layout);
            if (stageRef.current) {
                finishPanelHandoff(stageRef.current);
            }
            endStageResize();
        },
    });

    useLayoutEffect(() => () => cancelResize(drag, stageRef.current), [stageRef, stageWidth]);

    return (
        <div
            className="panel-resize-handle group touch-none"
            style={getPanelBoundaryStyle(boundaryStartPx)}
            data-boundary={boundaryIndex}
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize ${panel?.title ?? 'panel'}`}
            onPointerDown={onPointerDown}
            onDoubleClick={(): void => {
                if (panel && panel.id !== null) {
                    resetPanelWidth(panel.id);
                }
            }}
        >
            {/* The grip is the hover: a fill would paint the gap between two panels. */}
            <span
                className="panel-resize-handle-grip h-8 w-[3px] rounded-full bg-occ-foreground-tertiary opacity-0 group-hover:opacity-100"
                aria-hidden="true"
            />
        </div>
    );
};
